/**
 * SegmentationStatsPanel — displays nnInteractive segmentation metrics.
 *
 * Reads contour data from nnSegmentationResultAtom and computes volume,
 * per-axis extents, and slice count. Pure React — no vtk.js dependency.
 *
 * Replaces the previous 3D mesh viewer (vtk.js marching cubes) which was
 * removed because the coordinate alignment issue made the mesh unreliable
 * and the backend response already contains all the metrics we need.
 */

import React, { useMemo } from "react";
import { useAtomValue } from "jotai";
import { nnSegmentationResultAtom } from "../../atoms/segmentationAtoms";

export const SegmentationStatsPanel: React.FC = () => {
  const segResult = useAtomValue(nnSegmentationResultAtom);

  const metrics = useMemo(() => {
    if (!segResult) return null;
    const { contours, spacing } = segResult;
    const sliceIndices = Object.keys(contours)
      .map(Number)
      .sort((a, b) => a - b);
    const sliceCount = sliceIndices.length;
    if (sliceCount === 0) return null;

    let yMin = Infinity,
      yMax = -Infinity,
      xMin = Infinity,
      xMax = -Infinity;
    for (const sliceContours of Object.values(contours)) {
      for (const contour of sliceContours) {
        for (const [y, x] of contour) {
          yMin = Math.min(yMin, y);
          yMax = Math.max(yMax, y);
          xMin = Math.min(xMin, x);
          xMax = Math.max(xMax, x);
        }
      }
    }

    const zExtent =
      sliceCount > 1 ? (sliceIndices[sliceCount - 1] - sliceIndices[0]) * spacing[0] : spacing[0];
    const yExtent = (yMax - yMin) * spacing[1];
    const xExtent = (xMax - xMin) * spacing[2];

    return {
      volume_mm3: segResult.volume_mm3 ?? 0,
      max_diameter_mm: segResult.max_diameter_mm ?? 0,
      sliceCount,
      zExtent,
      yExtent,
      xExtent,
    };
  }, [segResult]);

  if (!segResult) {
    return (
      <div className="w-full h-full bg-bg-2 relative flex flex-col items-center justify-center text-fg-muted">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          className="mb-3 text-fg-muted"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
        <div className="text-sm font-medium text-fg-muted">Segmentation Stats</div>
        <div className="text-xs text-fg-muted mt-1">Click a stone to see measurements</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-bg-2 relative flex flex-col items-center justify-center">
      {metrics && (
        <div className="text-center">
          {/* Volume — primary metric */}
          <div className="text-3xl font-semibold text-signal-text-tint tabular-nums">
            {metrics.volume_mm3 >= 1000
              ? `${(metrics.volume_mm3 / 1000).toFixed(2)} cm³`
              : `${metrics.volume_mm3.toFixed(0)} mm³`}
          </div>
          <div className="text-xs text-fg-muted mt-1">Volume</div>

          {/* Max diameter */}
          <div className="text-xl font-medium text-fg mt-4 tabular-nums">
            {metrics.max_diameter_mm.toFixed(1)} mm
          </div>
          <div className="text-xs text-fg-muted mt-0.5">Max Diameter</div>

          {/* Per-axis extents */}
          <div className="mt-4 text-[11px] font-mono space-y-1">
            <div className="text-red-400">X: {metrics.xExtent.toFixed(1)} mm</div>
            <div className="text-green-400">Y: {metrics.yExtent.toFixed(1)} mm</div>
            <div className="text-blue-400">
              Z: {metrics.zExtent.toFixed(1)} mm · {metrics.sliceCount} slices
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
